import { Accordion, AccordionDetails, AccordionSummary, Box, Container, InputBase, Toolbar, Typography, alpha, styled } from '@mui/material';
import React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { useSearch } from '../hooks/useSearch';
import { ShowItem } from '../classes/showItem';
import ShowItems from '../components/ShowItems';
import Progress from '../components/Progress';
import { TopBarContext } from '../TopBar';
import { ErrorDialogContext } from '../components/ErrorDialog';
import { useSearchParams } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const SearchInput = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
  },
}));

let lastSearch = 0;

function Search() {
  const [search, setSearch] = React.useState<string | null>(null);
  const [results, setResults] = React.useState<ShowItem[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [disclaimerContent, setDisclaimerContent] = React.useState<string>('');
  const [presentazioneContent, setPresentazioneContent] = React.useState<string>('');
  const [allergieContent, setAllergieContent] = React.useState<string>('');
  const [contattiContent, setContattiContent] = React.useState<string>('');

  const [searchParams, setSearchParams] = useSearchParams();
  const [handleSearch] = useSearch();

  const topBarCtx = React.useContext(TopBarContext);
  const errorDialogCtx = React.useContext(ErrorDialogContext);

  React.useEffect(() => {
    topBarCtx?.setTitle('Cerca');
    
    fetch('/disclaimer_condizioni_uso.md')
      .then(response => response.text())
      .then(text => setDisclaimerContent(text))
      .catch(err => console.error('Error loading disclaimer:', err));
    
    fetch('/presentazione.md')
      .then(response => response.text())
      .then(text => setPresentazioneContent(text))
      .catch(err => console.error('Error loading presentazione:', err));
    
    fetch('/allergie_antibiotici.md')
      .then(response => response.text())
      .then(text => setAllergieContent(text))
      .catch(err => console.error('Error loading allergie:', err));
    
    fetch('/contatti.md')
      .then(response => response.text())
      .then(text => setContattiContent(text))
      .catch(err => console.error('Error loading contatti:', err));
  }, []);

  React.useEffect(() => {
    setLoading(true);
    handleSearch(search).then((res) => {
      setResults(res);
      setLoading(false);
    }).catch((err) => {
      errorDialogCtx?.setContent(err.message);
      errorDialogCtx?.setOpen(true);
      setLoading(false);
    });

    // if no new query comes in 1 second, set the search string as query parameter
    const n = lastSearch + 1;
    lastSearch = n;
    setTimeout(() => {
      if (n !== lastSearch) {
        // this search is outdated
        return;
      }

      // set the search string as query parameter
      if (search) {
        if (searchParams.has('q') && searchParams.get('q') === search) {
          return;
        }
        setSearchParams({ q: search });
      } else if (searchParams.has('q')) {
        setSearchParams({});
      }
    }, 1000);
  }, [search]);

  React.useEffect(() => {
    if (searchParams.has('q')) {
      setSearch(searchParams.get('q') as string);
    } else {
      setSearch(null);
    }
  }, [searchParams]);

  return (
    <React.Fragment>
        <Toolbar variant="dense" style={
        {
          background: 'var(--white)',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 3px 3px -2px',
        }
        }>
          <SearchInput onChange={
            (event) => {
              setSearch((event.target as HTMLInputElement).value);
            }
          }>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Cerca…"
              inputProps={{ 'aria-label': 'search', 'style': { width: '100%' }, 'value': search ?? '' }}
              style={{ width: '100%' }}
            />
          </SearchInput>
        </Toolbar>
      <Container>
        <Box sx={{ my: 2 }}>
          { results.length === 0 && !loading &&
          <React.Fragment>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                >
                  <Typography>Presentazione</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  '& p': { marginBottom: 2, fontSize: '0.875rem' },
                  '& strong': { fontWeight: 600 },
                  '& ul': { marginTop: 0, marginBottom: 2, paddingLeft: 3 },
                  '& li': { fontSize: '0.875rem' },
                  '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }
                }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {presentazioneContent}
                  </ReactMarkdown>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                >
                  <Typography>Altre Informazioni</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  '& p': { marginBottom: 2, fontSize: '0.875rem' },
                  '& strong': { fontWeight: 600 },
                  '& ul': { marginTop: 0, marginBottom: 2, paddingLeft: 3 },
                  '& li': { fontSize: '0.875rem' },
                  '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }
                }}>
                  <Typography variant="h6" gutterBottom>Disclaimer e condizioni d'uso</Typography>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {disclaimerContent}
                  </ReactMarkdown>
                  
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Allergie agli antibiotici</Typography>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {allergieContent}
                  </ReactMarkdown>
                  
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Contatti e Supporto</Typography>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {contattiContent}
                  </ReactMarkdown>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                >
                  <Typography>Report</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  '& p': { marginBottom: 2, fontSize: '0.875rem' },
                  '& ul': { marginTop: 0, marginBottom: 2, paddingLeft: 3 },
                  '& li': { fontSize: '0.875rem' },
                  '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }
                }}>
                  <ul>
                    <li><a href="https://www.regione.piemonte.it/web/media/44757/download" target="_blank" rel="noopener noreferrer">Consumo antibiotici in Regione Piemonte</a></li>
                    <li><a href="https://www.regione.piemonte.it/web/media/42927/download" target="_blank" rel="noopener noreferrer">AMR in Regione Piemonte - 1</a></li>
                    <li><a href="https://www.regione.piemonte.it/web/media/42928/download" target="_blank" rel="noopener noreferrer">AMR in Regione Piemonte - 2</a></li>
                  </ul>
                </Box>
              </AccordionDetails>
            </Accordion>
          </React.Fragment>
          }
          { loading && <Progress />}
          <ShowItems items={results} urlPrefix='/search' showScrollToTop />
        </Box>
      </Container>
    </React.Fragment>
  );
}

export default Search;
